import { LLM_CONFIG, ModelConfig } from '../../config';
import { SceneData, LLMResponse } from '../../types';
import { BaseLLMAdapter } from './BaseLLMAdapter';
import { ResponseProcessor } from '../../processors/response';
import { ReasoningVisualizer } from '../../components/ReasoningVisualizer';

/**
 * QWQ LLM适配器实现，处理QWQ相关的特殊逻辑
 */
export class QWQAdapter extends BaseLLMAdapter {
  private responseProcessor: ResponseProcessor;
  private reasoningVisualizer: ReasoningVisualizer;
  
  /**
   * 构造函数
   * @param config 模型配置
   * @param debugMode 是否处于调试模式
   */
  constructor(config: ModelConfig, debugMode: boolean = false) {
    super(config, debugMode);
    this.responseProcessor = new ResponseProcessor(debugMode);
    this.reasoningVisualizer = ReasoningVisualizer.getInstance();
  }
  
  /**
   * 准备QWQ请求
   */
  async prepareRequest(prompt: string): Promise<{
    endpoint: string;
    headers: Headers;
    body: any;
  }> {
    const endpoint = this.getEndpoint();
    const headers = this.createBaseHeaders();
    
    // 构建请求体
    const body = this.createBaseBody(prompt);
    
    // QWQ特殊配置
    body.stream = true;
    body.stream_options = {
      include_usage: true
    };
    
    // 添加系统消息
    body.messages.unshift(this.createSystemMessage(
      "你是一个赛博朋克2077风格游戏世界的创造者和叙事者。这是一个充满高科技和低生活的世界，霓虹与阴影并存，义体与人性交织。"
    ));
    
    // 显示推理可视化
    console.log('显示QWQ推理可视化');
    this.reasoningVisualizer.show(this.config.id, this.config.vendor || 'aliyun');
    
    this.logDebug('QWQ请求详情:', {
      endpoint,
      model: this.config.id,
      headers: Array.from(headers.entries()),
      body
    });
    
    return { endpoint, headers, body };
  }
  
  /**
   * 处理QWQ响应
   */
  async processResponse(response: Response): Promise<SceneData | null> {
    // 处理流式响应
    return this.handleStreamResponse(response);
  }
  
  /**
   * 处理流式响应
   */
  private async handleStreamResponse(response: Response): Promise<SceneData | null> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    let accumulatedContent = '';
    let reasoningContent = '';
    
    this.logDebug(`开始处理流式响应: 模型=${this.config.id}`, {});
    
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        // 将Uint8Array转换为字符串
        const chunk = new TextDecoder().decode(value);
        
        this.logDebug('流式响应数据片段:', chunk);
        
        // 处理SSE格式的数据
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ') && !line.includes('[DONE]')) {
            try {
              const jsonStr = line.substring(6); // 去掉 "data: "
              const jsonData = JSON.parse(jsonStr);
              
              this.logDebug('解析后的JSON数据:', jsonData);
              
              // 处理内容
              if (jsonData.choices?.[0]?.delta?.content !== undefined) {
                const content = jsonData.choices[0].delta.content;
                accumulatedContent += content;
                reasoningContent += content;
                
                // 更新推理可视化
                this.reasoningVisualizer.update(reasoningContent);
              }
            } catch (error) {
              console.error('解析SSE数据时出错:', error);
            }
          }
        }
      }
      
      this.logDebug('累积内容:', accumulatedContent);
      
      // 处理完成，隐藏推理可视化
      setTimeout(() => {
        this.reasoningVisualizer.hide();
      }, 1000);
      
      // 将累积的内容处理为SceneData
      const mockResponse: LLMResponse = {
        choices: [
          {
            message: {
              content: accumulatedContent
            }
          }
        ]
      };
      
      return this.responseProcessor.processResponse(mockResponse);
    } catch (error) {
      // 发生错误时隐藏推理可视化
      this.reasoningVisualizer.hide();
      
      console.error('处理流式响应时出错:', error);
      throw error;
    }
  }
} 